/**
 * Self-contained run selector for the compare page: pick 2–4 runs to line up. Options are
 * grouped by configuration so picking comparable runs (same config, different parameters) is
 * the natural path. Drives the `runs` URL param via the parent.
 */
import { useQuery } from "@tanstack/react-query";
import { Select, Tag, Tooltip } from "antd";
import {
  fetchBenchmarkRuns,
  unwrapCollection,
  type BenchmarkRun,
} from "@/lib/api/resources/benchmark";
import dayjs from "dayjs";
import { effectiveInferenceParams } from "@/components/ai-connection/inferenceOptionsOverride";
import { MAX_COMPARE_RUNS, RUN_COLORS } from "../lib/align";

const statusColor = (status: string): string =>
  status === "completed"
    ? "green"
    : status === "running" || status === "pending"
      ? "blue"
      : status === "failed"
        ? "red"
        : "default";

const runLabel = (run: BenchmarkRun): string =>
  run.benchmarkConfig?.name ||
  run.agent?.name ||
  run.id?.split("/").pop()?.slice(0, 8) ||
  "Run";

const runModel = (run: BenchmarkRun): string | undefined => {
  const config = (run.config ?? {}) as Record<string, any>;
  const p = config.parameters ?? {};
  return (
    effectiveInferenceParams(p).model ||
    config.agentAiConnection?.model ||
    p.override?.model ||
    run.agent?.aiConnection?.model ||
    undefined
  );
};

export const CompareRunPicker = ({
  selectedIds,
  onChange,
}: {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}) => {
  const { data, isLoading } = useQuery({
    queryKey: ["benchmark_runs"],
    queryFn: () => fetchBenchmarkRuns(),
  });

  const runs = unwrapCollection<BenchmarkRun>(data).filter(
    (r) =>
      r.status === "completed" || r.status === "failed" || r.completedCases > 0,
  );

  // Group by config so the dropdown clusters runs that are actually comparable.
  const groups = new Map<string, BenchmarkRun[]>();
  for (const r of runs) {
    const g = r.benchmarkConfig?.name || r.agent?.name || "Ungrouped";
    (groups.get(g) ?? groups.set(g, []).get(g)!).push(r);
  }

  const options = Array.from(groups.entries()).map(([group, items]) => ({
    label: group,
    title: group,
    options: items.map((r) => {
      const id = r.id?.split("/").pop() ?? r.id;
      const model = runModel(r);
      return {
        value: id,
        label: (
          <span className="flex items-center justify-between gap-2">
            <span className="truncate">
              {runLabel(r)}{" "}
              {model && (
                <span className="text-gray-500 text-xs font-mono">
                  · {model}{" "}
                </span>
              )}
              <span className="text-gray-400 text-xs">
                · {dayjs(r.createdAt).format("DD-MM HH:mm")}
              </span>
            </span>
            <Tag color={statusColor(r.status)} className="!m-0 !text-[10px]">
              {r.status}
            </Tag>
          </span>
        ),
        // Plain-text fallback so search + the collapsed tag stay readable.
        text: `${runLabel(r)} ${model ?? ""} ${dayjs(r.createdAt).format("DD-MM HH:mm")}`,
      };
    }),
  }));

  const atLimit = selectedIds.length >= MAX_COMPARE_RUNS;

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <Select
        mode="multiple"
        loading={isLoading}
        className="min-w-[28rem] flex-grow shadow-sm hover:shadow-md transition-shadow duration-300"
        placeholder={`Select 2–${MAX_COMPARE_RUNS} runs to compare…`}
        value={selectedIds}
        onChange={(ids: string[]) => onChange(ids.slice(0, MAX_COMPARE_RUNS))}
        options={options}
        maxCount={MAX_COMPARE_RUNS}
        optionFilterProp="text"
        size="large"
        tagRender={(props) => {
          const idx = selectedIds.indexOf(String(props.value));
          return (
            <Tag
              closable={props.closable}
              onClose={props.onClose}
              className="px-3 py-1 font-semibold text-xs border border-transparent shadow-sm flex items-center gap-1.5 transition-all hover:brightness-110"
              style={{
                background: `linear-gradient(to right, ${RUN_COLORS[idx] || "#3b82f6"}15, ${RUN_COLORS[idx] || "#3b82f6"}05)`,
                borderColor: `${RUN_COLORS[idx] || "#3b82f6"}40`,
                color: RUN_COLORS[idx] || "#3b82f6",
                borderRadius: "6px",
              }}
            >
              <div
                className="w-1.5 h-1.5 rounded-full"
                style={{ backgroundColor: RUN_COLORS[idx] || "#3b82f6" }}
              />
              {props.label}
            </Tag>
          );
        }}
      />
      {atLimit && (
        <Tooltip
          title={`Up to ${MAX_COMPARE_RUNS} runs keep the comparison readable.`}
        >
          <span className="text-xs text-amber-600">
            Max {MAX_COMPARE_RUNS} runs
          </span>
        </Tooltip>
      )}
    </div>
  );
};

export default CompareRunPicker;
